import { isWhitespaceOrEmptyString } from '../../../predicates/string';
import { isNull, isNullOrUndefined, isUndefined } from '../../../predicates/type';
import { formatDecimalScale, isInt, isMinMaxNumber, isPinCode, isStrictNumber, toNumber } from '../../../utils/number';
import {
	BaseAtomFn,
	FloatAtomFn,
	FloatFormatAtomProps,
	FloatNormalizerAtomProps,
	FormatNumberAtomFn,
	FulfilledValueIndicatorFn,
	NormalizerTransformerFn,
	NormalizerValueType,
	NumberAtomFn,
	NumberNormalizerAtomProps,
	PrimitiveValidatorsFn,
} from '../types';

const numericFulfilledValueIndicator: FulfilledValueIndicatorFn<NumberNormalizerAtomProps> = function (value, options) {
	const stringedValue = String(value);

	if (
		(options?.allowNegativeValue && stringedValue.length === 1 && stringedValue === '-') ||
		(!options?.forbidBlank && isWhitespaceOrEmptyString(value))
	) {
		return false;
	}

	return true;
};

const decimalFulfilledValueIndicator: FulfilledValueIndicatorFn<FloatNormalizerAtomProps> = function (value, options) {
	options = options || {};
	const numberAtomResult = numericFulfilledValueIndicator(value, options);

	if (!numberAtomResult) {
		return false;
	}

	const stringedValue = String(value);
	const matchDot = stringedValue.match(/([,.])/);

	if (options?.allowNegativeValue && stringedValue[0] === '-' && stringedValue.match(/0$/)) {
		return false;
	}

	if (!isNull(matchDot)) {
		if (matchDot.index === stringedValue.length - 1) {
			return false;
		}

		if (stringedValue.match(/0$/)) {
			return false;
		}
	}

	return true;
};

const toNumberAtom: BaseAtomFn = function ({ value }) {
	const result = toNumber(value);

	return {
		value: result,
		success: isStrictNumber(result),
	};
};

const toPinCodeAtom: BaseAtomFn = function ({ value }) {
	return {
		value,
		success: isPinCode(value),
	};
};

const minMaxAtom: NumberAtomFn = function ({ value }, options) {
	const result = isMinMaxNumber(value, {
		min: options?.min,
		max: options?.max,
	});

	return {
		value,
		success: result,
	};
};

const positiveValueValidator: PrimitiveValidatorsFn = function (value) {
	return isNull(String(value).match(/-/g));
};

const forbidWrongRangeValidator: PrimitiveValidatorsFn<NormalizerValueType, NumberNormalizerAtomProps> = function (
	value,
	options,
) {
	const parsedValue = Number(String(value).replace(',', '.'));

	if (!isStrictNumber(parsedValue)) {
		return true;
	}

	if (!isUndefined(options.max) && parsedValue > options.max) {
		return false;
	}

	return true;
};

const forbidNaNValidator: PrimitiveValidatorsFn<NormalizerValueType, NumberNormalizerAtomProps> = function (value) {
	let stringedValue = String(value).replace(',', '.');
	const matchExtraMinus = stringedValue.match(/-/g);

	if (
		(stringedValue.indexOf('-') !== -1 && isNull(stringedValue.match(/^-/))) ||
		(!isNull(matchExtraMinus) && matchExtraMinus.length > 1)
	) {
		return false;
	}

	stringedValue = stringedValue.replace('-', '');
	const parsedValue = Number(stringedValue);
	const isStrict = isStrictNumber(parsedValue);

	if (!isStrict) {
		return false;
	}

	if (stringedValue.indexOf('.') === -1) {
		return true;
	}

	return isNull(stringedValue.match(/^0{2}\.*/g));
};

const commaCountValidator: PrimitiveValidatorsFn<NormalizerValueType, { digitsAfterDot?: number }> = function (
	value,
	options,
) {
	const stringedValue = String(value);
	const floatSignMatch = stringedValue.match(/[,.]/);

	if (isNull(floatSignMatch) || isUndefined(floatSignMatch.index) || isUndefined(options.digitsAfterDot)) {
		return true;
	}

	return stringedValue.slice(floatSignMatch.index + 1).length <= options.digitsAfterDot;
};

const isIntAtom: NumberAtomFn = function ({ value }) {
	const result = isInt(value);

	return {
		value: value,
		success: result,
	};
};

const commaCountAtom: FloatAtomFn = function ({ value }, options) {
	const stringedValue = String(value);
	const floatSignMatch = stringedValue.match(/[,.]/);

	if (isNull(floatSignMatch) || isUndefined(floatSignMatch.index)) {
		return {
			value,
			success: true,
		};
	}

	return {
		value,
		success: stringedValue.slice(floatSignMatch.index + 1).length <= options?.digitsAfterDot!,
	};
};

const commaToDisplayFormat: FormatNumberAtomFn = function (value) {
	const stringedValue = String(value);

	if (isNullOrUndefined(value) || isWhitespaceOrEmptyString(stringedValue)) {
		return {
			value: '',
			success: false,
		};
	}

	return {
		value: stringedValue.replace('.', ','),
		success: true,
	};
};

const formatZeroCountAfterDotAtom: FormatNumberAtomFn<FloatFormatAtomProps> = function (value, options) {
	return {
		value: formatDecimalScale(value, options?.decimalScale || 0),
		success: true,
	};
};

const skipNonNumericSymbolsTransformer: NormalizerTransformerFn = function (value) {
	const stringValue = String(value);
	const allowedSymbolsRegExp = /[^\d\-.,]+/g;

	return stringValue.replace(allowedSymbolsRegExp, '');
};

const allowDigitsOnlyTransformer: NormalizerTransformerFn = function (value) {
	const stringValue = String(value);
	const allowedSymbolsRegExp = /\D+/g;

	return stringValue.replace(allowedSymbolsRegExp, '');
};

const commaToNumberFormatTransformer: NormalizerTransformerFn = function (value) {
	if (isNullOrUndefined(value)) {
		return value;
	}

	let stringedValue = String(value).replace(',', '.');

	if (stringedValue.match(/^\./)) {
		stringedValue = `0${stringedValue}`;
	}

	if (stringedValue.match(/^-\./)) {
		stringedValue = `-0.${stringedValue.slice(2)}`;
	}

	return stringedValue;
};

export {
	minMaxAtom,
	toNumberAtom,
	isIntAtom,
	commaToNumberFormatTransformer,
	commaCountAtom,
	numericFulfilledValueIndicator,
	decimalFulfilledValueIndicator,
	commaToDisplayFormat,
	formatZeroCountAfterDotAtom,
	toPinCodeAtom,
	skipNonNumericSymbolsTransformer,
	allowDigitsOnlyTransformer,
	positiveValueValidator,
	commaCountValidator,
	forbidWrongRangeValidator,
	forbidNaNValidator,
};
